import React, { useEffect, useRef } from 'react';
import { useAppContext } from 'context/AppContext';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { searchRoundReports, setSearchParameters } from 'actions/admin/roundReports';
import { performSearch, mergeSearchParameters } from 'components/adminTable/AdminTableHelper';
import routes from 'routes';
import { startOfMonth, endOfMonth } from 'date-fns';
import { formatIsoDate } from 'helpers/DateHelper';

import Loader from 'common/Loader';
import Box from '@material-ui/core/Box';
import AdminPage from '../../presentational/AdminPage';
import AdminContentWrapper from '../../presentational/AdminContentWrapper';
import SearchRoundReportsForm from './SearchRoundReportsForm';
import AdminTable from 'components/adminTable/AdminTable';
import ShortDateTime from 'common/ShortDateTime';

const RoundReports = () => {
    const { appContext } = useAppContext();
    const dispatch = useDispatch();
    const location = useLocation();
    const history = useHistory();

    const searchResult = useSelector(state => state.admin.roundReports.searchResult);
    const searchParametersFromState = useSelector(state => state.admin.roundReports.searchParameters);

    const defaultParameters = {
        page: 0,
        pageSize: 20,
        createdTimeFrom: formatIsoDate(startOfMonth(new Date()), appContext),
        createdTimeTo: formatIsoDate(endOfMonth(new Date()), appContext),
        q: null,
        sortByColumn: 'createdTime',
        sortDirection: 'asc'
    };

    const columns = [
        {
            title: 'ID',
            id: 'id',
            render: item => <Link to={routes.roundReport.replace(':roundReportId', item.id).replace(':token', item.token)}>{item.id}</Link>
        },
        {
            title: 'Storage site',
            id: 'storageSiteTitle',
            render: item => <Link to={routes.admin.storageSite.replace(':storageSiteId', item.storageSiteId)}>{item.storageSiteTitle}</Link>
        },
        {
            title: 'Owner actor',
            id: 'ownerActorName',
            render: item => <Link to={routes.admin.actor.replace(':actorId', item.ownerActorid)}>{item.ownerActorName}</Link>
        },
        {
            title: 'Created',
            id: 'createdTime',
            render: item => <ShortDateTime value={item.createdTime} />
        },
        {
            title: 'Completed',
            id: 'completedTime',
            render: item => <ShortDateTime value={item.completedTime} />
        },
        {
            title: 'Deviations',
            id: 'occupiedInRealityButNotInSystemCount',
            render: item => item.occupiedInRealityButNotInSystemCount ? item.occupiedInRealityButNotInSystemCount : ''
        },
        {
            title: 'Notes',
            id: 'noteCount',
            render: item => item.noteCount ? item.noteCount : ''
        }
    ];

    const search = parameters => {
        const sanitizedParameters = { ...parameters };
        if(parameters.createdTimeFrom) {
            sanitizedParameters.createdTimeFrom = formatIsoDate(parameters.createdTimeFrom, appContext);
        }
        if(parameters.createdTimeFrom) {
            sanitizedParameters.createdTimeTo = formatIsoDate(parameters.createdTimeTo, appContext);
        }
        const searchParameters = mergeSearchParameters(sanitizedParameters, location, searchParametersFromState, defaultParameters);
        performSearch(performSearchObject, searchParameters);
    };

    const getParametersForApiCall = parameters => ({
        ...parameters
    });

    const performSearchObject = {
        searchParametersFromState,
        setSearchParametersToState: parameters => dispatch(setSearchParameters(parameters)),
        searchResult,
        search,
        apiCall: parameters => dispatch(searchRoundReports(getParametersForApiCall(parameters))),
        location,
        history,
        isInitializedRef: useRef()
    };

    useEffect(() => {
        const searchParameters = mergeSearchParameters({}, location, searchParametersFromState, defaultParameters);
        performSearch(performSearchObject, searchParameters);
    }, []);

    const handleSearchFormSubmit = formValues => {
        search({ ...defaultParameters, ...formValues, page: 0 });
    };

    const handleSort = sortingParameters => {
        search(sortingParameters);
    };

    const handlePageChange = newPage => {
        search({ page: newPage });
    };

    const handlePageChangeSize = newPageSize => {
        search({ page: 0, pageSize: newPageSize });
    };

    return (
        <AdminPage title="Round reports">
            <AdminContentWrapper type="listing">

                <SearchRoundReportsForm
                    searchParameters={searchParametersFromState}
                    onSubmit={handleSearchFormSubmit}
                />

                <Box>
                    {
                        searchResult.isLoading && <Loader />
                    }
                    {
                        searchResult.items && (
                            <AdminTable
                                isPaged
                                items={searchResult.items}
                                count={searchResult.count}
                                page={searchResult.page}
                                pageSize={searchResult.pageSize}
                                columns={columns}
                                sortByColumn={columns.find(c => c.id === searchParametersFromState.sortByColumn)}
                                sortDirection={searchParametersFromState.sortDirection}
                                onSort={handleSort}
                                onPageChange={handlePageChange}
                                onPageChangeSize={handlePageChangeSize}
                            />
                        )
                    }
                </Box>

            </AdminContentWrapper>
        </AdminPage>
    );
};

export default RoundReports;
